/**
 * The street map as one journey (MT-01, LO-02): every city is a segment of one tall world, the
 * home city at the bottom and the others stacked above it in progression order. Pure data and
 * geometry (no Phaser, no app), so the scene's rules are unit-tested.
 *
 * World y grows downwards. A segment is its street with its name band under it: the journey
 * climbs, so a city begins at the band (its banner) and its level 1 sits just above that. The
 * anchor segment (the city the map was entered on) keeps its top at y = 0, so a city pack that
 * registers while the map is up moves the cities around it, never the one under the player.
 */

/** play: the player can be there; the rest are teasers (a silhouette street, a lock, the way in). */
export type StopKind = 'play' | 'locked' | 'download' | 'update';

export interface Stop {
  city: string;
  kind: StopKind;
  /** `locked`: the city to finish first */
  after: string | null;
}

export interface CityFacts {
  id: string;
  /** progression order (catalog `order`) */
  order: number;
  /** a city pack registered at runtime (LO-02); the rest ship with the app */
  pack: boolean;
  /** the pack needs a newer app (roadmap-3k §8.1) */
  needsUpdate: boolean;
}

/**
 * The journey, bottom → top: the home city, the other cities by progression order, then
 * published packs not on this device (`download`). A pack plays once it is on the device (as
 * its city tab used to); a built-in city opens when `opens(city, prev)` says the player
 * finished the built-in city before it (the server opens the next shop in that order too).
 */
export function journeyStops(home: string, cities: readonly CityFacts[], opens: (city: string, prev: string) => boolean, downloadable: readonly string[] = []): Stop[] {
  const out: Stop[] = [{ city: home, kind: 'play', after: null }];
  let prev: string | null = null;
  for (const c of [...cities].sort((a, b) => a.order - b.order)) {
    if (c.id === home) {
      prev = home;
      continue;
    }
    if (out.some((s) => s.city === c.id)) continue;
    if (c.pack) out.push({ city: c.id, kind: c.needsUpdate ? 'update' : 'play', after: null });
    else {
      out.push(prev === null || opens(c.id, prev) ? { city: c.id, kind: 'play', after: null } : { city: c.id, kind: 'locked', after: prev });
      prev = c.id;
    }
  }
  for (const id of downloadable) if (!out.some((s) => s.city === id)) out.push({ city: id, kind: 'download', after: null });
  return out;
}

/**
 * The city the map opens on: the last active one while the player can still be there (a
 * playable stop); else the furthest open city with levels still to beat; else the home city.
 */
export function resumeCity(stops: readonly Stop[], last: string | null, unbeaten: (city: string) => boolean): string {
  if (last && stops.some((s) => s.city === last && s.kind === 'play')) return last;
  for (let i = stops.length - 1; i > 0; i--) if (stops[i]!.kind === 'play' && unbeaten(stops[i]!.city)) return stops[i]!.city;
  return stops[0]!.city;
}

export interface World {
  /** world y of each segment's top; index 0 is the home city (the bottom) */
  readonly tops: readonly number[];
  readonly heights: readonly number[];
  /** world y of the topmost segment's top and of the home segment's bottom */
  readonly top: number;
  readonly bottom: number;
}

/** Stack segments of these heights (index 0 at the bottom) with the anchor's top at y = 0. */
export function layoutWorld(heights: readonly number[], anchor: number): World {
  const n = heights.length;
  const a = Math.min(Math.max(0, anchor), n - 1);
  const tops = new Array<number>(n);
  tops[a] = 0;
  for (let i = a + 1; i < n; i++) tops[i] = tops[i - 1]! - heights[i]!;
  for (let i = a - 1; i >= 0; i--) tops[i] = tops[i + 1]! + heights[i + 1]!;
  return { tops, heights: [...heights], top: tops[n - 1]!, bottom: tops[0]! + heights[0]! };
}

/** The segment holding world y; above the world it is the top one, below it the home one. */
export function segmentAt(w: World, y: number): number {
  for (let i = w.tops.length - 1; i > 0; i--) if (y < w.tops[i]! + w.heights[i]!) return i;
  return 0;
}

/** Screen rows (device px) of the visible stage: under the DOM top bar, above the Play row. */
export interface Stage {
  top: number;
  bottom: number;
}

/**
 * Camera scroll limits: the top of the world may come a little way under the top bar; the
 * home street's bottom rests on the Play row.
 */
export function clampScroll(w: World, s: Stage, y: number): number {
  const min = w.top - s.top * 0.6;
  const max = Math.max(min, w.bottom - s.bottom);
  return Math.min(max, Math.max(min, y));
}

/** World y at the centre of the stage for a camera scroll. */
export function stageCentre(s: Stage, scrollY: number): number {
  return scrollY + (s.top + s.bottom) / 2;
}

/** Camera scroll that centres world y in the stage (clamped). */
export function focusScroll(w: World, s: Stage, worldY: number): number {
  return clampScroll(w, s, worldY - (s.top + s.bottom) / 2);
}

/**
 * The active city: the playable segment at the stage centre. Over a teaser (or anything else
 * not playable) the previous one stays, so Play keeps pointing somewhere the player can go.
 */
export function activeSegment(w: World, playable: readonly boolean[], centre: number, prev: number): number {
  const i = segmentAt(w, centre);
  return playable[i] ? i : prev;
}

/**
 * Glide speeds in CSS px per ms (the scene scales them to device px): a finger lifting slower
 * than MIN is no fling, a glide stops below STOP, and starts at most at MAX (about a phone
 * screen of travel).
 */
export const GLIDE_MIN = 0.15;
export const GLIDE_STOP = 0.02;
export const GLIDE_MAX = 3;

/** The active city counts as switched to once the view has rested on it this long (analytics). */
export const SETTLE_MS = 1500;

/**
 * The city to report a switch to, if any: the active one, once the view has rested `restingMs`
 * on it and it is not the one reported last. Passing through cities while scrolling reports none.
 */
export function settledSwitch(active: string, reported: string, restingMs: number): string | null {
  return active !== reported && restingMs >= SETTLE_MS ? active : null;
}

/** Fling speed after `dt` ms of glide: exponential decay with a 325 ms time constant (iOS lists feel). */
export function glide(v: number, dt: number): number {
  return v * Math.exp(-dt / 325);
}

/**
 * How much of the map's right edge the feature rail takes, in CSS px, before any safe-area inset:
 * its 64 px buttons, their 8 px margin (ui/styles.css `.map-rail`) and a little air. On a phone the
 * street runs under the rail, so canvas text that must stay readable (the pinned header, the
 * first-run hint) keeps clear of it by `railClearance`, which adds the inset the rail sits inside.
 */
export const RAIL_CLEAR = 84;

/**
 * The strip the rail takes from the right edge, in CSS px, on a screen whose right edge has a
 * safe-area inset (a landscape notch takes 44-59 px): the rail sits inside the inset
 * (`right: calc(var(--safe-r) + 8px)`), so it reaches that much further in than `RAIL_CLEAR`.
 */
export function railClearance(rightInset: number): number {
  return RAIL_CLEAR + rightInset;
}

/**
 * Centre x for a label `w` wide that would like to sit at `x`, kept on the screen (`pad` from its
 * left edge) and left of the feature rail (`rail` from the right edge). Every length shares one
 * unit; `W` is the screen's width. A label wider than the room starts at `pad`.
 */
export function clearOfRail(x: number, w: number, W: number, rail: number, pad: number): number {
  return Math.max(pad + w / 2, Math.min(x, W - rail - w / 2));
}
